import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { Card, PageHeader } from "../components/account-ui.tsx";
import { OrderLines, OrderTotals } from "../components/legacy/OrderSummary.tsx";
import { OrderStatusBadge } from "../components/status.tsx";
import { CheckCircleIcon } from "../icons/index.tsx";
import { api, type Order } from "../lib/api.ts";
import { CheckoutSteps } from "./checkout.$campaignId.tsx";

export const Route = createFileRoute("/checkout/confirmation/$code")({ component: Confirmation });

function Confirmation() {
  const { code } = Route.useParams();
  const { data } = useQuery({
    queryKey: ["order", code],
    queryFn: () =>
      api.get<{
        order: Order;
        items: {
          kind: string;
          description: string;
          quantity: number;
          unit_cents: number;
          total_cents: number;
        }[];
      }>(`/orders/${code}`),
    // The Stripe webhook flips pending -> paid a few seconds after redirect.
    refetchInterval: (q) => (q.state.data?.order.status === "pending" ? 3000 : false),
  });
  const queryClient = useQueryClient();
  const status = data?.order.status;
  // Paying takes the campaign out of the cart and adds the order to history.
  useEffect(() => {
    if (status && status !== "pending") {
      void queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
    }
  }, [status, queryClient]);

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl">
        <PageHeader
          title="Order received"
          subtitle={data ? `Order ${data.order.code}` : "Loading your order"}
        />
        <CheckoutSteps current="CONFIRMATION" />
        {!data ? (
          <p className="text-sm text-gray-500">Loading order…</p>
        ) : (
          <Card>
            <div className="flex items-start gap-4">
              <CheckCircleIcon forcedClass="h-10 w-10 shrink-0 text-green-600" />
              <div className="min-w-0">
                <p className="text-lg font-semibold text-gray-900">
                  Order {data.order.code} received
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <OrderStatusBadge status={data.order.status} />
                  {data.order.status === "pending" && (
                    <span className="text-sm text-gray-500">Confirming payment…</span>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-6">
              <OrderLines items={data.items} campaignId={data.order.campaign_id} />
              <OrderTotals order={data.order} />
            </div>
            <div className="mt-2 flex flex-wrap gap-3">
              <Link
                to="/account/orders"
                className="inline-flex items-center justify-center rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700"
              >
                View Purchase History
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Back to Products
              </Link>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
